// NodeSequenceField.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodeSequenceField.css';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * NodeSequenceField
 *
 * Editable string field for Sequence values (auto-assigned on create).
 *
 * @param {object} node - Node object (unused; kept for NodeField parity).
 * @param {string} value - Current field value.
 * @param {string} fieldLabel - Label for the field.
 * @param {Function} onChange - Callback when new text is set.
 * @param {boolean} [readOnly]
 * @param {boolean} [previewMode]
 *
 * @returns {ReactNode} NodeSequenceField.
 */
const NodeSequenceField = ({
  node: _node,
  fieldLabel,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  // ----- STATE ----- //

  const [text, setText] = useState(value ?? '');

  // ----- EFFECTS ----- //

  useEffect(() => {
    setText(value ?? '');
  }, [value]);

  // ----- HANDLERS ----- //

  /**
   * Handles text change.
   *
   * @param {*} event
   */
  const handleTextChange = useCallback((event) => {
    if (isReadOnly) return;
    setText(event?.target?.value ?? '');
  }, [isReadOnly]);

  /**
   * Handles text blur.
   */
  const handleTextBlur = useCallback(() => {
    if (isReadOnly || typeof onChange !== 'function') return;
    onChange(text);
  }, [isReadOnly, onChange, text]);

  // ----- RENDER ----- //

  return (
    <div className="node-sequence-field-container">
      <TextInput
        value={text}
        placeholder={fieldLabel || 'Sequence'}
        startIconKey="tag"
        startIconColor={theme.foregroundMuted}
        iconSize={22}
        hybrid
        expandable
        maxLength={120}
        readOnly={isReadOnly}
        onBlur={handleTextBlur}
        onChange={isReadOnly ? undefined : handleTextChange}
      />
    </div>
  );
};

// Dev Tools
NodeSequenceField.displayName = 'NodeSequenceField';

export default NodeSequenceField;
